// The two tiles at the top of Sort my searches (P82, D198 of documentation/pr-plans/insights-simplify-plan.md;
// sort-my-searches/tiles.blade.php): the sorted tile and the clicks tile share one state, whether the funnel shows.
//
// - The eye in the clicks tile shows and hides the funnel (st_toggle()). Showing it grows the sorted tile to the
//   funnel's height and turns its small ring into the donut; hiding it reverses both. The motion is the tiles' own CSS
//   transitions on the `view` token, under motion-safe, so with reduced motion everything takes its place at once.
// - The donut's parts say their name, share and impressions in a tooltip while one is hovered or holds focus
//   (st_show()); each part's words are on its own element, which the server draws on every render, so the tooltip
//   never says a figure from before a count.
//
// - The open funnel has two views (D205): the Funnel, and the Flow of where its clicks come from (st_setView()). A swap
//   glides the three bands from the funnel's rows to the Flow's small funnel and back, the rest of the view coming in
//   behind them (glide()), so the two read as one picture; under reduced motion the views simply swap.
//
// Open on every visit (P93, D209; Nathan, 23 Oct: "My only concern is that they might not see it"): the server draws it
// open, so nothing moves as the page starts. The view it opens to is the one last chosen in this browser, a convenience
// kept in storage that may be refused (a private window), so the Funnel is the fallback.
//
// - Once the board scrolls the summary past, it pins as its thin bar under the Insights header (st_placeBar()): the
//   tiles' one line, % sorted and each search intent's clicks, on the page's colour, without Regroup AI topics and the
//   eye, whose answers draw where the summary stands; chips that do not fit scroll sideways, so the bar stays one line.
//   It takes the place of the summary's last strip (isBarPinned()), and the hold after it keeps the rest of the
//   summary's height (barHold()), so nothing under it moves; scrolling back puts the summary back as it was. The swap
//   is classes only, never x-show, which shows a frame late, and the tiles' transitions are off for it (st_still). Only
//   where the tiles share one row (from 1,180px): stacked, the bar would cover a third of a phone's window under the
//   header. It never pins over keyboard focus it would hide, nor puts the summary back over a chip that holds it.
// - The bar's height is on the document element (--insights-sort-bar-height) while the tiles can pin, so the board's
//   head and its drag edge, and the page's scroll margins, clear it as they clear the header; a chip's ⋯, the peek and
//   a focused chip clear it through data-insights-sticks, while it is pinned (insights/covered-top.js, P89).

import motion from "../motion-tokens.json" with { type: "json" };
import { motionAllowed } from "./insights/motion";

const VIEW_KEY = "insights-sort-clicks-view";
const VIEWS = ["funnel", "flow"];
// Where the tiles share one row (sort-my-searches.blade.php), the only width the bar pins at.
const PINS_FROM = "(min-width: 1180px)";
const BAR_HEIGHT = "--insights-sort-bar-height";

export default function InsightsSortTiles() {
    // Kept out of the component's reactive state, as insights-top-bar.js keeps its observer: an observer wrapped in
    // Alpine's proxy would be called on the proxy, which the browser refuses.
    let observer = null;
    let frame = null;

    return {
        // Whether the summary is open, the eye's state: the funnel and the donut show while it is open and in its place.
        st_open: true,

        // Which of its views the open funnel shows: 'funnel' or 'flow'.
        st_view: "funnel",

        // Whether the summary stands pinned as its thin bar.
        st_pinned: false,

        // While pinned, where the summary stood: its top in its section and its height.
        st_held: null,

        // While pinned, the hold's height, barHold()'s, and the column's gap it takes back.
        st_hold: 0,
        st_gap: 0,

        // True for the frame the summary and the bar swap: the tiles' transitions are off, so each takes its place at once.
        st_still: false,

        st_isOpen() {
            return this.st_open && !this.st_pinned;
        },

        init() {
            try {
                const kept = window.localStorage.getItem(VIEW_KEY);

                if (VIEWS.includes(kept)) {
                    this.st_view = kept;
                }
            } catch {
                // Storage refused: the Funnel, as on a first visit.
            }
        },

        st_setView(view) {
            if (!VIEWS.includes(view) || view === this.st_view) {
                return;
            }

            const root = this.$root;
            const from = bandPlaces(root);

            this.st_view = view;
            this.st_part = null;
            // x-show swaps the views after Alpine's tick: the bands are measured on the frame after, where the new view
            // stands in its place, and the glide starts before that frame paints.
            this.$nextTick(() => requestAnimationFrame(() => glide(root, from, view)));

            try {
                window.localStorage.setItem(VIEW_KEY, view);
            } catch {
                // Storage refused: the choice holds for this visit only.
            }
        },

        // The part whose tooltip shows, as its element's words: {key, name, line, color, x, y}; null for none.
        st_part: null,

        st_toggle() {
            this.st_open = !this.st_open;

            if (!this.st_open) {
                this.st_part = null;
            }
        },

        // A part hovered or focused: its tooltip shows over the middle of its arc. Only on the donut: the small ring is
        // a picture of the line beside it.
        st_show(el) {
            if (!this.st_isOpen() || !el?.dataset) {
                return;
            }

            const { part, name, line, color, x, y } = el.dataset;

            this.st_part = { key: part, name, line, color, x, y };
        },

        st_hide(el) {
            if (this.st_part !== null && this.st_part.key === el?.dataset?.part) {
                this.st_part = null;
            }
        },

        st_isShown(key) {
            return this.st_part !== null && this.st_part.key === key;
        },

        // Every other part fades while one is shown, so the one said stands out.
        st_isFaded(key) {
            return this.st_part !== null && this.st_part.key !== key;
        },

        // A scroll anywhere on the page, a resize, or the bar changing size asks where the summary stands, once a frame.
        st_placeSoon() {
            frame ??= requestAnimationFrame(() => {
                frame = null;
                this.st_placeBar();
            });
        },

        st_placeBar() {
            const row = this.$refs.row;

            if (!row?.isConnected || row.offsetParent === null || !window.matchMedia(PINS_FROM).matches) {
                this.st_unpin();
                document.documentElement.style.removeProperty(BAR_HEIGHT);

                return;
            }

            const bar = barOf(row, this.st_pinned);
            const covered = document.querySelector("[data-insights-header]")?.getBoundingClientRect().bottom ?? 0;
            const section = this.$root.parentElement;
            const top = section.getBoundingClientRect().top;

            document.documentElement.style.setProperty(BAR_HEIGHT, `${barHeight(bar)}px`);

            if (this.st_pinned) {
                this.st_hold = barHold(this.st_held.height, bar);

                if (!isBarPinned(top + this.st_held.top + this.st_held.height, covered, bar)) {
                    this.st_unpin();
                }

                return;
            }

            const box = row.getBoundingClientRect();

            if (isBarPinned(box.bottom, covered, bar) && !hidesFocus(row)) {
                this.st_held = { top: box.top - top, height: box.height };
                this.st_hold = barHold(box.height, bar);
                this.st_gap = parseFloat(getComputedStyle(section).rowGap) || 0;
                this.st_swap(true);
                // The bar as drawn, once Alpine has drawn the swap (its microtask is queued first), before anything paints:
                // the hold and the height then match the bar's own, wherever the line it was told by is shorter.
                queueMicrotask(() => {
                    if (this.st_pinned) {
                        const drawn = barOf(row, true);

                        this.st_hold = barHold(this.st_held.height, drawn);
                        document.documentElement.style.setProperty(BAR_HEIGHT, `${barHeight(drawn)}px`);
                    }
                });

                if (typeof ResizeObserver === "function") {
                    observer ??= new ResizeObserver(() => this.st_placeSoon());
                    observer.observe(row);
                }
            }
        },

        // Back in its place as it was, unless a chip of the bar holds keyboard focus: then it comes back closed, so the
        // chip stays, and the eye opens it again. x-show draws the chips a frame late, so the bar keeps them until then.
        st_unpin() {
            if (!this.st_pinned) {
                return;
            }

            if (chipHoldsFocus(this.$refs.row)) {
                this.st_open = false;

                if (this.$refs.row.querySelector("[data-sort-chips]")?.style.display === "none") {
                    this.st_placeSoon();

                    return;
                }
            }

            observer?.disconnect();
            this.st_swap(false);
        },

        st_swap(pinned) {
            this.st_still = true;
            this.st_pinned = pinned;
            this.st_part = null;

            if (!pinned) {
                this.st_held = null;
                this.st_hold = 0;
            }

            // Transitions back on once a frame has drawn the swap with none.
            requestAnimationFrame(() => requestAnimationFrame(() => {
                this.st_still = false;
            }));
        },

        destroy() {
            observer?.disconnect();

            if (frame !== null) {
                cancelAnimationFrame(frame);
            }

            document.documentElement.style.removeProperty(BAR_HEIGHT);
        },
    };
}

/**
 * Whether the bar stands pinned (P93, D209): once the bottom of the summary's place in the window (bottom) is no lower
 * than where the bar's tiles would end under the Insights header (covered, the header's bottom), the bar takes the place
 * of the summary's last strip, its tiles exactly where a closed summary's stood, so nothing under it is covered and a
 * closed summary pins as it reaches the header. bar: the tiles' height and the room above and below them, in px.
 */
export function isBarPinned(bottom, covered, bar) {
    return bottom <= covered + bar.room + bar.tiles;
}

/**
 * The hold's height under the pinned bar: the rest of the summary's height (height), since the bar's own place in the
 * page is its tiles' (its negative margins take back the room above and below them), so nothing under it moves as the
 * two swap. A closed summary is the tiles alone, so it holds nothing.
 */
export function barHold(height, bar) {
    return height - bar.tiles;
}

// The bar's whole height, the room above and below its tiles included: what the board's head and the scroll margins add
// to the header's.
export function barHeight(bar) {
    return bar.tiles + 2 * bar.room;
}

// The bar as it is or will be drawn: the room above and below its tiles, the row's --sort-bar-room; and the tiles'
// height, the row's own while pinned, else the sorted tile drawn as its one line (data-sort-line), which never wraps.
// That is the bar's height, and never more: the clicks tile's line is the same unless it says the read failed, and a
// taller bar only pins over a few more pixels, where a shorter one told as taller would pin and unpin by turns.
function barOf(row, isPinned) {
    const room = parseFloat(getComputedStyle(row).getPropertyValue("--sort-bar-room")) || 0;

    if (isPinned) {
        return { tiles: row.offsetHeight - 2 * room, room };
    }

    const tile = row.querySelector("[data-sort-sorted]");
    const style = getComputedStyle(tile);
    const line = tile.querySelector("[data-sort-line]").offsetHeight;

    return { tiles: line + tile.offsetHeight - tile.clientHeight + parseFloat(style.paddingTop) + parseFloat(style.paddingBottom), room };
}

// Keyboard focus on something the bar does not draw (the eye, Regroup AI topics, the open funnel or the donut): pinning
// would take it away, so the summary scrolls on as part of the page until focus leaves it.
function hidesFocus(row) {
    const focused = document.activeElement;

    return row.contains(focused) && focused.matches(":focus-visible") && focused.closest("[data-sort-chips]") === null;
}

function chipHoldsFocus(row) {
    const focused = document.activeElement;

    return row.contains(focused) && focused.matches(":focus-visible") && focused.closest("[data-sort-chips]") !== null;
}

// Each band showing, by its search intent, with its box on the page.
function bandPlaces(root) {
    const places = {};

    root.querySelectorAll("[data-glide]").forEach((band) => {
        const box = band.getBoundingClientRect();

        if (box.width > 0 && box.height > 0) {
            places[band.dataset.glide] = box;
        }
    });

    return places;
}

// The swap's motion: each band of the view now showing starts from where its namesake stood in the view before and
// glides to its own place on the `view` token, scaled from that box to its own, while the rest of the new view
// (data-glide-fade) fades in behind it on the `show` token. Transforms and opacity only, so nothing is laid out again
// mid-move; the elements end exactly as drawn.
function glide(root, from, view) {
    if (!motionAllowed()) {
        return;
    }

    root.querySelectorAll("[data-glide]").forEach((band) => {
        const was = from[band.dataset.glide];
        const now = band.getBoundingClientRect();

        if (was === undefined || now.width === 0 || now.height === 0) {
            return;
        }

        const start = `translate(${was.left - now.left}px, ${was.top - now.top}px) scale(${was.width / now.width}, ${was.height / now.height})`;

        band.animate([{ transformOrigin: "0 0", transform: start }, { transformOrigin: "0 0", transform: "none" }], {
            duration: motion.duration.view,
            easing: motion.ease,
        });
    });

    root.querySelectorAll(`[data-glide-fade="${view}"]`).forEach((part) => {
        part.animate([{ opacity: 0 }, { opacity: 1 }], {
            duration: motion.duration.show,
            delay: motion.duration.press,
            easing: motion.ease,
            fill: "backwards",
        });
    });
}
